import React, { useState, useEffect } from 'react'
import { Modal as AntModal } from 'antd'
import type { ModalProps as AntModalProps } from 'antd'
import { createRoot } from 'react-dom/client'
import './style.scss'

export interface ModalProps extends AntModalProps {
  /** 确认按钮文本 */
  okText?: string
  /** 取消按钮文本 */
  cancelText?: string
  /** 是否显示底部按钮 */
  showFooter?: boolean
  /** 自定义底部按钮 */
  footer?: React.ReactNode
  /** 确认按钮点击事件 */
  onOk?: () => void
  /** 取消按钮点击事件 */
  onCancel?: () => void
  /** 弹窗内容 */
  content?: React.ReactNode
  /** 是否显示弹窗 */
  open?: boolean
}

interface ModalInterface extends React.FC<ModalProps> {
  confirm: typeof AntModal.confirm
  info: typeof AntModal.info
  success: typeof AntModal.success
  warning: typeof AntModal.warning
  error: typeof AntModal.error
}

const BaseModal: React.FC<ModalProps> = ({
  title,
  content,
  width = 560,
  okText = '确定',
  cancelText = '取消',
  showFooter = true,
  footer,
  onOk,
  onCancel,
  open = true,
  children,
  ...restProps
}) => {
  return (
    <AntModal
      title={title}
      width={width}
      footer={footer}
      onCancel={onCancel}
      open={open}
      onOk={onOk}
      okText={okText}
      cancelText={cancelText}
      {...restProps}
    >
      {content || children}
    </AntModal>
  )
}

// 函数调用方式
const showModal = (props: ModalProps) => {
  const div = document.createElement('div')
  document.body.appendChild(div)
  const root = createRoot(div)

  const destroy = () => {
    root.unmount()
    if (div.parentNode) {
      div.parentNode.removeChild(div)
    }
  }

  const ModalContent = () => {
    const [visible, setVisible] = useState(true)

    return (
      <BaseModal
        {...props}
        open={visible}
        onCancel={() => {
          setVisible(false)
          destroy()
          props.onCancel?.()
        }}
        onOk={() => {
          setVisible(false)
          destroy()
          props.onOk?.()
        }}
      />
    )
  }

  root.render(<ModalContent />)

  return {
    destroy,
    update: (newProps: Partial<ModalProps>) => {
      Object.assign(props, newProps)
      root.render(<ModalContent />)
    },
  }
}

const Modal = Object.assign(BaseModal, {
  confirm: AntModal.confirm,
  info: AntModal.info,
  success: AntModal.success,
  warning: AntModal.warning,
  error: AntModal.error,
  show: (props: ModalProps) => {
    if (typeof props === 'object' && !React.isValidElement(props)) {
      return showModal(props)
    }
    return <BaseModal {...props} />
  },
}) as ModalInterface & {
  show: (
    props: ModalProps,
  ) =>
    | { destroy: () => void; update: (props: Partial<ModalProps>) => void }
    | JSX.Element
}

export { Modal }
